import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import {
  X,
  Check,
  ChevronRight,
  ChevronLeft,
  User,
  Settings,
  Plug,
  Copy,
  ExternalLink,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Store,
  ShoppingCart,
  BarChart2,
  Zap,
  AlertTriangle,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface OnboardingSession {
  id: string;
  seller_id?: number;
  current_step: number;
  status: string;
  steps_data: Record<string, unknown>;
}

/** Rol de `auth_roles` tal como lo devuelve GET /rbac/roles. */
interface WizardRole {
  id: number;
  key: string;
  name: string;
  is_admin: boolean;
}

interface IntegrationItem {
  service: string;
  label: string;
  status: 'pending' | 'configured' | 'skipped';
  icon: React.ReactNode;
}

interface Props {
  onClose: () => void;
  onComplete: () => void;
}

// ─── Step indicator ───────────────────────────────────────────────────────────

const STEPS = [
  { num: 1, label: 'Seller ML', icon: <Store size={16} /> },
  { num: 2, label: 'Configuración', icon: <Settings size={16} /> },
  { num: 3, label: 'Integraciones', icon: <Plug size={16} /> },
  { num: 4, label: 'Plantilla', icon: <Copy size={16} /> },
  { num: 5, label: 'Activar', icon: <Zap size={16} /> },
];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center justify-center gap-1 py-4 px-6">
      {STEPS.map((s, i) => {
        const done = s.num < current;
        const active = s.num === current;
        return (
          <div key={s.num} className="flex items-center">
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors
                  ${done ? 'bg-[var(--color-primary)] text-white' : ''}
                  ${active ? 'bg-[var(--color-primary)] text-white ring-2 ring-[var(--color-primary)] ring-offset-2 ring-offset-[var(--color-surface)]' : ''}
                  ${!done && !active ? 'bg-[var(--color-border)] text-[var(--color-text-muted)]' : ''}`}
              >
                {done ? <Check size={14} /> : s.icon}
              </div>
              <span
                className={`text-[10px] font-medium hidden sm:block ${active ? 'text-[var(--color-primary)]' : 'text-[var(--color-text-muted)]'}`}
              >
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={`h-0.5 w-8 sm:w-12 mx-1 mb-4 rounded transition-colors ${
                  s.num < current ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-border)]'
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function OnboardingWizard({ onClose, onComplete }: Props) {
  const { sellers } = useUIStore();
  const isAdminActor = useAuthStore((s) => s.user?.is_admin ?? false);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [session, setSession] = useState<OnboardingSession | null>(null);

  // Step 1
  const [nickname, setNickname] = useState('');
  const [email, setEmail] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [authUrl, setAuthUrl] = useState('');
  const [mlConnected, setMlConnected] = useState(false);
  const [mlProfile, setMlProfile] = useState<Record<string, unknown> | null>(null);
  const [polling, setPolling] = useState(false);

  // Step 2
  const [markupFactor, setMarkupFactor] = useState('2.5');
  const [defaultStock, setDefaultStock] = useState('50');
  const [warehouseGroupId, setWarehouseGroupId] = useState('1');
  // Wizard de bodegas (Fase 0 multi-seller)
  const [wgMode, setWgMode] = useState<'create' | 'existing'>('create');
  const [wgName, setWgName] = useState('');
  const [wgDescription, setWgDescription] = useState('');
  const [availableGroups, setAvailableGroups] = useState<{ id: number; name: string; description: string | null }[]>([]);
  const [activeSites, setActiveSites] = useState<string[]>([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roles, setRoles] = useState<WizardRole[]>([]);
  const [roleId, setRoleId] = useState<number | null>(null);

  // Step 3
  const [integrations, setIntegrations] = useState<IntegrationItem[]>([
    { service: 'amazon_business', label: 'Amazon Business', status: 'pending', icon: <ShoppingCart size={18} /> },
    { service: 'amazon_purchase', label: 'Amazon Purchase API', status: 'pending', icon: <ShoppingCart size={18} /> },
    { service: 'keepa', label: 'Keepa (Precios)', status: 'pending', icon: <BarChart2 size={18} /> },
  ]);
  const [integrationNote, setIntegrationNote] = useState('');

  // Step 4
  const [templateSellerId, setTemplateSellerId] = useState('');
  const [tasksCloned, setTasksCloned] = useState(false);

  // Polling ref
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setPolling(false);
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  // Cargar warehouse_groups para el selector del Step 2
  useEffect(() => {
    api.get<{ id: number; name: string; description: string | null }[]>('/onboarding/warehouse-groups')
      .then(res => {
        if (res.success && Array.isArray(res.data)) {
          setAvailableGroups(res.data);
        }
      })
      .catch(() => { /* silent */ });
  }, []);

  // Cargar los roles disponibles para el usuario del vendedor (Step 2).
  // Solo un Administrador puede elegir el rol Administrador.
  useEffect(() => {
    api.get<WizardRole[]>('/rbac/roles')
      .then(res => {
        if (!res.success || !Array.isArray(res.data)) return;
        const list = res.data.filter(r => !r.is_admin || isAdminActor);
        setRoles(list);
        setRoleId(prev => {
          if (prev !== null && list.some(r => r.id === prev)) return prev;
          const preferred = list.find(r => r.key === 'warehouse') ?? list.find(r => !r.is_admin) ?? list[0];
          return preferred ? preferred.id : null;
        });
      })
      .catch(() => { /* silent */ });
  }, [isAdminActor]);

  // ─── Step 1 handlers ────────────────────────────────────────────────────────

  async function handleCreateSeller() {
    if (!nickname || !email || !clientId || !clientSecret) {
      setError('Completa todos los campos para crear el seller.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await api.post<{ seller: { id: number }; session: OnboardingSession }>('/onboarding/start', {
        nickname,
        email,
        client_id: clientId,
        client_secret: clientSecret,
      });
      if (res.success && res.data?.session) {
        setSession(res.data.session);
      } else {
        setError(res.error || 'Error al crear el seller.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleGenerateAuthUrl() {
    if (!session) return;
    setError('');
    setLoading(true);
    try {
      const res = await api.post<{ auth_url: string }>(`/onboarding/${session.id}/ml-auth-url`, {});
      if (res.success && res.data) {
        setAuthUrl(res.data.auth_url);
        startPolling(session.id);
      } else {
        setError(res.error || 'Error al generar el link.');
      }
    } finally {
      setLoading(false);
    }
  }

  function startPolling(sessionId: string) {
    setPolling(true);
    pollIntervalRef.current = setInterval(async () => {
      const res = await api.get<OnboardingSession>(`/onboarding/${sessionId}`);
      if (res.success && res.data) {
        setSession(res.data);
        if (res.data.current_step >= 2 && res.data.steps_data?.step1) {
          stopPolling();
          setMlConnected(true);
          const step1 = res.data.steps_data.step1 as Record<string, unknown>;
          setMlProfile(step1);
        }
      }
    }, 3000);
  }

  async function handleCopyUrl() {
    if (authUrl) {
      await navigator.clipboard.writeText(authUrl);
    }
  }

  // ─── Step 2 handler ─────────────────────────────────────────────────────────

  async function handleSaveStep2() {
    if (!session) return;
    setError('');

    // Validación de bodega
    if (wgMode === 'create' && !wgName.trim()) {
      setError('El nombre de la nueva bodega es obligatorio.');
      return;
    }
    if (wgMode === 'existing' && !warehouseGroupId) {
      setError('Seleccioná una bodega existente.');
      return;
    }
    if (!roleId) {
      setError('Elegí un rol para el usuario del vendedor.');
      return;
    }

    setLoading(true);
    try {
      const body: Record<string, unknown> = {
        markup_factor: parseFloat(markupFactor),
        default_stock: parseInt(defaultStock, 10),
        active_sites: activeSites,
        warehouse_group_mode: wgMode,
        user: {
          username,
          password,
          display_name: displayName,
          role_id: roleId,
        },
      };
      if (wgMode === 'create') {
        body.warehouse_group_name = wgName.trim();
        body.warehouse_group_description = wgDescription.trim() || null;
      } else {
        body.warehouse_group_id = parseInt(warehouseGroupId, 10);
      }
      const res = await api.put<{ warehouse_group_id?: number }>(`/onboarding/${session.id}/step/2`, body);
      if (res.success) {
        // Si fue create, refrescamos la lista por si vuelve a entrar al step
        if (wgMode === 'create' && res.data?.warehouse_group_id) {
          setWarehouseGroupId(String(res.data.warehouse_group_id));
        }
        setStep(3);
      } else {
        setError(res.error || 'Error al guardar configuración.');
      }
    } finally {
      setLoading(false);
    }
  }

  // ─── Step 3 handler ─────────────────────────────────────────────────────────

  async function handleSaveStep3() {
    if (!session) return;
    setError('');
    setLoading(true);
    try {
      const res = await api.put<{ ok: boolean }>(`/onboarding/${session.id}/step/3`, {
        integrations: integrations.map((i) => ({ service: i.service, status: i.status })),
      });
      if (res.success) {
        setStep(4);
      } else {
        setError(res.error || 'Error al guardar integraciones.');
      }
    } finally {
      setLoading(false);
    }
  }

  function handleSkipIntegration(service: string) {
    setIntegrations((prev) =>
      prev.map((i) => (i.service === service ? { ...i, status: 'skipped' } : i))
    );
  }

  function handleConfigureIntegration(service: string) {
    setIntegrationNote(
      `La integración "${service}" se puede configurar desde Ajustes → Conexiones una vez que el seller esté activo.`
    );
    handleSkipIntegration(service);
  }

  // ─── Step 4 handler ─────────────────────────────────────────────────────────

  async function handleCloneTasks() {
    if (!session || !templateSellerId) return;
    setError('');
    setLoading(true);
    try {
      const res = await api.put<{ ok: boolean }>(`/onboarding/${session.id}/step/4`, {
        template_seller_id: parseInt(templateSellerId, 10),
      });
      if (res.success) {
        setTasksCloned(true);
      } else {
        setError(res.error || 'Error al clonar tareas.');
      }
    } finally {
      setLoading(false);
    }
  }

  // ─── Step 5 handler ─────────────────────────────────────────────────────────

  async function handleActivate() {
    if (!session) return;
    setError('');
    setLoading(true);
    try {
      const res = await api.post<{ seller_id: number }>(`/onboarding/${session.id}/activate`, {});
      if (res.success) {
        onComplete();
      } else {
        setError(res.error || 'Error al activar el seller.');
      }
    } finally {
      setLoading(false);
    }
  }

  // ─── Input class helper ──────────────────────────────────────────────────────

  const inputClass =
    'w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm text-[var(--color-text)] outline-none focus:border-[var(--color-primary)]';

  // ─── Can advance? ────────────────────────────────────────────────────────────

  const canNext = (): boolean => {
    if (step === 1) return mlConnected;
    if (step === 2) return true; // will validate on submit
    if (step === 3) return integrations.every((i) => i.status !== 'pending');
    if (step === 4) return true;
    return false;
  };

  // ─── Render steps ────────────────────────────────────────────────────────────

  function renderStep1() {
    return (
      <div className="space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-text)] mb-1">
            Conectar Seller de MercadoLibre
          </h2>
          <p className="text-sm text-[var(--color-text-secondary)]">
            Ingresá las credenciales de la app ML para autorizar el acceso.
          </p>
        </div>

        {/* Form */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
              Nickname
            </label>
            <input
              className={inputClass}
              placeholder="ej: MISELLER"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              disabled={!!session}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
              Email
            </label>
            <input
              type="email"
              className={inputClass}
              placeholder="admin@ejemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!!session}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
              Client ID (App ML)
            </label>
            <input
              className={inputClass}
              placeholder="Client ID"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              disabled={!!session}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
              Client Secret (App ML)
            </label>
            <input
              type="password"
              className={inputClass}
              placeholder="Client Secret"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              disabled={!!session}
            />
          </div>
        </div>

        {!session && (
          <button
            onClick={handleCreateSeller}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Store size={16} />}
            Crear Seller
          </button>
        )}

        {session && !authUrl && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-green-600 font-medium">
              <CheckCircle2 size={16} />
              Seller creado — ID de sesión: <code className="bg-[var(--color-bg)] px-1.5 py-0.5 rounded text-xs">{session.id}</code>
            </div>
            <button
              onClick={handleGenerateAuthUrl}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <ExternalLink size={16} />}
              Generar Link de Autorización
            </button>
          </div>
        )}

        {authUrl && !mlConnected && (
          <div className="space-y-3">
            <p className="text-sm text-[var(--color-text-secondary)]">
              Compartí este link con el seller para que autorice la app:
            </p>
            <div className="flex items-center gap-2 p-3 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg">
              <span className="flex-1 text-xs text-[var(--color-text)] break-all font-mono">{authUrl}</span>
              <button
                onClick={handleCopyUrl}
                className="shrink-0 p-1.5 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)]"
                title="Copiar"
              >
                <Copy size={14} />
              </button>
              <a
                href={authUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 p-1.5 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)]"
                title="Abrir en nueva pestaña"
              >
                <ExternalLink size={14} />
              </a>
            </div>
            <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
              <Loader2 size={14} className="animate-spin" />
              Esperando autorización de ML...
            </div>
          </div>
        )}

        {mlConnected && (
          <div className="p-4 rounded-lg border border-green-500/30 bg-green-500/10 space-y-2">
            <div className="flex items-center gap-2 text-green-600 font-semibold">
              <CheckCircle2 size={18} />
              ¡Seller conectado exitosamente a MercadoLibre!
            </div>
            {mlProfile && (
              <div className="text-sm text-[var(--color-text-secondary)]">
                {mlProfile.nickname ? (
                  <span>Cuenta: <strong>{String(mlProfile.nickname)}</strong></span>
                ) : (
                  <span>Conexión verificada.</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  function renderStep2() {
    const SITES = ['MLA', 'MLB', 'MLM', 'MLC', 'MCO', 'MLU'];
    const SITE_FLAGS: Record<string, string> = {
      MLA: '🇦🇷',
      MLB: '🇧🇷',
      MLM: '🇲🇽',
      MLC: '🇨🇱',
      MCO: '🇨🇴',
      MLU: '🇺🇾',
    };

    const toggleSite = (site: string) => {
      setActiveSites((prev) =>
        prev.includes(site) ? prev.filter((s) => s !== site) : [...prev, site]
      );
    };

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-text)] mb-1">
            Configuración inicial
          </h2>
          <p className="text-sm text-[var(--color-text-secondary)]">
            Ajustá los parámetros operativos y creá el usuario del dashboard.
          </p>
        </div>

        {/* Config */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide">
            Parámetros de operación
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                Factor de markup
              </label>
              <input
                type="number"
                step="0.1"
                min="1"
                className={inputClass}
                value={markupFactor}
                onChange={(e) => setMarkupFactor(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                Stock por defecto
              </label>
              <input
                type="number"
                min="0"
                className={inputClass}
                value={defaultStock}
                onChange={(e) => setDefaultStock(e.target.value)}
              />
            </div>
          </div>

          {/* Bodega: crear nueva o usar existente */}
          <div className="space-y-3 border border-[var(--color-border)] rounded-lg p-4">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide">
                Bodega de stock
              </h4>
              <span className="text-[10px] text-[var(--color-text-muted)]">
                Determina si comparte inventario con otro seller o tiene depósito propio
              </span>
            </div>
            <div className="flex gap-3">
              <label className={`flex-1 flex items-start gap-2 p-3 rounded-lg cursor-pointer border transition-colors ${
                wgMode === 'create'
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5'
                  : 'border-[var(--color-border)]'
              }`}>
                <input
                  type="radio"
                  className="mt-1"
                  checked={wgMode === 'create'}
                  onChange={() => setWgMode('create')}
                />
                <div>
                  <div className="text-sm font-medium">Crear nueva bodega</div>
                  <div className="text-xs text-[var(--color-text-muted)]">
                    Recomendado: stock aislado, sin mezcla con otros sellers
                  </div>
                </div>
              </label>
              <label className={`flex-1 flex items-start gap-2 p-3 rounded-lg cursor-pointer border transition-colors ${
                wgMode === 'existing'
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5'
                  : 'border-[var(--color-border)]'
              }`}>
                <input
                  type="radio"
                  className="mt-1"
                  checked={wgMode === 'existing'}
                  onChange={() => setWgMode('existing')}
                />
                <div>
                  <div className="text-sm font-medium">Usar bodega existente</div>
                  <div className="text-xs text-[var(--color-text-muted)]">
                    Comparte stock físico con otro(s) seller(s) ya configurados
                  </div>
                </div>
              </label>
            </div>

            {wgMode === 'create' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                    Nombre de la bodega *
                  </label>
                  <input
                    className={inputClass}
                    placeholder="Bodega [Nombre Seller]"
                    value={wgName}
                    onChange={(e) => setWgName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                    Descripción (opcional)
                  </label>
                  <input
                    className={inputClass}
                    placeholder="Ubicación, encargado, etc."
                    value={wgDescription}
                    onChange={(e) => setWgDescription(e.target.value)}
                  />
                </div>
              </div>
            )}

            {wgMode === 'existing' && (
              <div>
                <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                  Bodega existente
                </label>
                <select
                  className={inputClass}
                  value={warehouseGroupId}
                  onChange={(e) => setWarehouseGroupId(e.target.value)}
                >
                  {availableGroups.length === 0 && (
                    <option value="">— Sin bodegas disponibles —</option>
                  )}
                  {availableGroups.map((g) => (
                    <option key={g.id} value={g.id}>
                      #{g.id} · {g.name}{g.description ? ` (${g.description})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Sites */}
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-2">
              Sites activos
            </label>
            <div className="flex flex-wrap gap-2">
              {SITES.map((site) => (
                <button
                  key={site}
                  onClick={() => toggleSite(site)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                    activeSites.includes(site)
                      ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                      : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)]'
                  }`}
                >
                  <span>{SITE_FLAGS[site]}</span>
                  <span>{site}</span>
                  {activeSites.includes(site) && <Check size={12} />}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Dashboard User */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide">
            Usuario del dashboard
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                Nombre de usuario
              </label>
              <input
                className={inputClass}
                placeholder="usuario"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                Contraseña
              </label>
              <input
                type="password"
                className={inputClass}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
                Esta contraseña es de un solo uso: el usuario deberá cambiarla en su primer
                ingreso.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                Nombre visible
              </label>
              <input
                className={inputClass}
                placeholder="Juan Pérez"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                Rol
              </label>
              <select
                className={inputClass}
                value={roleId ?? ''}
                onChange={(e) => setRoleId(e.target.value ? Number(e.target.value) : null)}
                disabled={roles.length === 0}
              >
                {roles.length === 0 && <option value="">Cargando roles…</option>}
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderStep3() {
    const statusIcon = (status: IntegrationItem['status']) => {
      if (status === 'configured') return <CheckCircle2 size={16} className="text-green-500" />;
      if (status === 'skipped') return <AlertCircle size={16} className="text-yellow-500" />;
      return <XCircle size={16} className="text-[var(--color-text-muted)]" />;
    };

    return (
      <div className="space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-text)] mb-1">
            Integraciones externas
          </h2>
          <p className="text-sm text-[var(--color-text-secondary)]">
            Configurá o saltá cada integración. Las que salteás se pueden configurar luego desde Ajustes.
          </p>
        </div>

        <div className="space-y-3">
          {integrations.map((integ) => (
            <div
              key={integ.service}
              className="flex items-center gap-4 p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)]"
            >
              <div className="text-[var(--color-text-secondary)]">{integ.icon}</div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {statusIcon(integ.status)}
                  <span className="text-sm font-medium text-[var(--color-text)]">{integ.label}</span>
                </div>
                <span className="text-xs text-[var(--color-text-muted)]">
                  {integ.status === 'pending' && 'Sin configurar'}
                  {integ.status === 'configured' && 'Configurado'}
                  {integ.status === 'skipped' && 'Saltado — configurar desde Ajustes'}
                </span>
              </div>
              {integ.status === 'pending' && (
                <div className="flex gap-2">
                  <button
                    onClick={() => handleConfigureIntegration(integ.service)}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10"
                  >
                    Configurar
                  </button>
                  <button
                    onClick={() => handleSkipIntegration(integ.service)}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)]"
                  >
                    Saltar
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {integrationNote && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-sm text-blue-600">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{integrationNote}</span>
          </div>
        )}
      </div>
    );
  }

  function renderStep4() {
    return (
      <div className="space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-text)] mb-1">
            Plantilla de automatizaciones
          </h2>
          <p className="text-sm text-[var(--color-text-secondary)]">
            Copiá las tareas programadas de un seller existente para no empezar desde cero.
            También podés saltear este paso.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
              Seller plantilla
            </label>
            <select
              className={inputClass}
              value={templateSellerId}
              onChange={(e) => setTemplateSellerId(e.target.value)}
              disabled={tasksCloned}
            >
              <option value="">— Seleccionar seller —</option>
              {sellers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nickname} (ID {s.id})
                </option>
              ))}
            </select>
          </div>

          {tasksCloned ? (
            <div className="flex items-center gap-2 text-green-600 font-medium text-sm">
              <CheckCircle2 size={16} />
              Tareas clonadas exitosamente desde seller {templateSellerId}.
            </div>
          ) : (
            <button
              onClick={handleCloneTasks}
              disabled={!templateSellerId || loading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Copy size={16} />}
              Clonar tareas
            </button>
          )}

          <p className="text-xs text-[var(--color-text-muted)]">
            Si no querés clonar tareas, hacé clic en "Siguiente" directamente.
          </p>
        </div>
      </div>
    );
  }

  function renderStep5() {
    return (
      <div className="space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-text)] mb-1">
            Resumen y activación
          </h2>
          <p className="text-sm text-[var(--color-text-secondary)]">
            Revisá los datos antes de activar el seller.
          </p>
        </div>

        <div className="space-y-3">
          {/* Step 1 summary */}
          <div className="p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] space-y-1">
            <div className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text)]">
              <CheckCircle2 size={15} className="text-green-500" />
              Paso 1 — Conexión ML
            </div>
            <div className="text-sm text-[var(--color-text-secondary)] pl-5">
              <span className="font-medium">{nickname}</span> · {email}
            </div>
            {mlConnected && (
              <div className="text-xs text-green-600 pl-5">MercadoLibre autorizado</div>
            )}
          </div>

          {/* Step 2 summary */}
          <div className="p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] space-y-1">
            <div className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text)]">
              <CheckCircle2 size={15} className="text-green-500" />
              Paso 2 — Configuración
            </div>
            <div className="text-sm text-[var(--color-text-secondary)] pl-5">
              Markup ×{markupFactor} · Stock {defaultStock} · Sites: {activeSites.join(', ') || '—'}
            </div>
            {username && (
              <div className="text-xs text-[var(--color-text-muted)] pl-5">
                Usuario: {username} ({roles.find((r) => r.id === roleId)?.name || 'sin rol'})
              </div>
            )}
          </div>

          {/* Step 3 summary */}
          <div className="p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] space-y-1">
            <div className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text)]">
              <CheckCircle2 size={15} className="text-green-500" />
              Paso 3 — Integraciones
            </div>
            <div className="text-sm text-[var(--color-text-secondary)] pl-5">
              {integrations.map((i) => (
                <span key={i.service} className="mr-3">
                  {i.label}:{' '}
                  <span
                    className={
                      i.status === 'configured'
                        ? 'text-green-600'
                        : i.status === 'skipped'
                        ? 'text-yellow-500'
                        : 'text-[var(--color-text-muted)]'
                    }
                  >
                    {i.status === 'configured' ? 'Configurado' : i.status === 'skipped' ? 'Saltado' : 'Pendiente'}
                  </span>
                </span>
              ))}
            </div>
          </div>

          {/* Step 4 summary */}
          <div className="p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] space-y-1">
            <div className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text)]">
              {tasksCloned ? (
                <CheckCircle2 size={15} className="text-green-500" />
              ) : (
                <AlertTriangle size={15} className="text-yellow-500" />
              )}
              Paso 4 — Plantilla
            </div>
            <div className="text-sm text-[var(--color-text-secondary)] pl-5">
              {tasksCloned
                ? `Tareas clonadas del seller ID ${templateSellerId}`
                : 'Sin plantilla seleccionada'}
            </div>
          </div>
        </div>

        <button
          onClick={handleActivate}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-500 disabled:opacity-50 text-sm"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Zap size={18} />}
          Activar Seller
        </button>
      </div>
    );
  }

  // ─── Navigation ──────────────────────────────────────────────────────────────

  async function handleNext() {
    setError('');
    if (step === 2) {
      await handleSaveStep2();
      return;
    }
    if (step === 3) {
      await handleSaveStep3();
      return;
    }
    if (step < 5) {
      setStep((s) => s + 1);
    }
  }

  function handleBack() {
    setError('');
    if (step > 1) setStep((s) => s - 1);
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[var(--color-surface)] rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-[var(--color-border)]">
        {/* Sticky header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-2 border-b border-[var(--color-border)] shrink-0">
          <div className="flex items-center gap-2">
            <User size={20} className="text-[var(--color-primary)]" />
            <span className="font-semibold text-[var(--color-text)]">Nuevo Seller — Onboarding</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]"
          >
            <X size={18} />
          </button>
        </div>

        {/* Step indicator */}
        <div className="border-b border-[var(--color-border)] shrink-0">
          <StepIndicator current={step} />
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && (
            <div className="mb-4 flex items-center gap-2 p-3 rounded-lg bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 text-[var(--color-error)] text-sm">
              <XCircle size={16} className="shrink-0" />
              {error}
            </div>
          )}

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          {step === 5 && renderStep5()}
        </div>

        {/* Sticky footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-[var(--color-border)] shrink-0 bg-[var(--color-surface)]">
          <button
            onClick={handleBack}
            disabled={step === 1}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[var(--color-border)] text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] disabled:opacity-40"
          >
            <ChevronLeft size={16} />
            Atrás
          </button>

          <span className="text-xs text-[var(--color-text-muted)]">
            Paso {step} de {STEPS.length}
          </span>

          {step < 5 ? (
            <button
              onClick={handleNext}
              disabled={!canNext() || loading}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              Siguiente
              <ChevronRight size={16} />
            </button>
          ) : (
            <div className="w-24" /> /* spacer to keep layout balanced on step 5 */
          )}
        </div>
      </div>
    </div>
  );
}
